export function formatXLabel(periodStart) {
  const date = new Date(periodStart + 'T00:00:00');
  return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
}

function addPerPeriod(acc, add) {
  const map = new Map(acc.map((p) => [p.period_start, { ...p }]));
  for (const p of add) {
    const prev = map.get(p.period_start) ?? {
      period_start:     p.period_start,
      actual_cents:     null,
      forecast_cents:   null,
      budget_cents:     null,
      actual_ht_cents:  null,
      forecast_ht_cents: null,
    };
    for (const k of ['actual_cents', 'forecast_cents', 'budget_cents', 'actual_ht_cents', 'forecast_ht_cents']) {
      if (p[k] != null) prev[k] = (prev[k] ?? 0) + p[k];
    }
    map.set(p.period_start, prev);
  }
  return [...map.values()];
}

export function buildTree(categories, byCategoryMap, periodStarts) {
  const childrenByParent = new Map();
  for (const c of categories) {
    if (c.parent_id) {
      if (!childrenByParent.has(c.parent_id)) childrenByParent.set(c.parent_id, []);
      childrenByParent.get(c.parent_id).push(c);
    }
  }

  const emptyPerPeriod = () =>
    periodStarts.map((ps) => ({
      period_start:      ps,
      actual_cents:      null,
      forecast_cents:    null,
      budget_cents:      null,
      actual_ht_cents:   null,
      forecast_ht_cents: null,
    }));

  function buildNode(cat) {
    const rawChildren = childrenByParent.get(cat.id) ?? [];
    const children = rawChildren.map(buildNode);

    const own = byCategoryMap.get(cat.id);
    let per_period = own ? own.per_period : emptyPerPeriod();

    for (const child of children) {
      per_period = addPerPeriod(per_period, child.per_period);
    }

    const directHit = !!own;
    const childHit  = children.some((c) => c.hasActivity);

    return {
      id:         cat.id,
      name:       cat.name,
      parent_id:  cat.parent_id,
      flow_type:  own?.flow_type ?? cat.flow_type ?? 'expense',
      children:   children
        .filter((c) => c.hasActivity)
        .sort((a, b) => totalMagnitude(b) - totalMagnitude(a)),
      per_period,
      hasActivity: directHit || childHit,
    };
  }

  const roots = categories
    .filter((c) => !c.parent_id)
    .map(buildNode)
    .filter((r) => r.hasActivity)
    .sort((a, b) => totalMagnitude(b) - totalMagnitude(a));

  return roots;
}

export function totalMagnitude(node) {
  return node.per_period.reduce((acc, p) => {
    const v = p.actual_cents ?? p.forecast_cents ?? p.budget_cents ?? 0;
    return acc + Math.abs(v);
  }, 0);
}

export function collectDescendantIds(node, acc = []) {
  acc.push(node.id);
  for (const c of node.children ?? []) collectDescendantIds(c, acc);
  return acc;
}

export function formatPeriodRange(periodStart, periodEnd) {
  const start = new Date(periodStart + 'T00:00:00');
  const end   = new Date(periodEnd + 'T00:00:00');
  const sameDay = start.toDateString() === end.toDateString();
  if (sameDay) {
    return start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  }
  return `${start.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} → ${end.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}

export function buildCols(buckets) {
  return buckets.map((b) => ({
    period_start: b.period_start,
    period_end:   b.period_end,
    is_future:    b.is_future,
    // Non-null quand le prévisionnel du mois est figé (snapshot) → cadenas
    frozen_at:    b.frozen_at ?? null,
    shortLabel:   formatXLabel(b.period_start),
  }));
}

export function buildNodeIndex(tree) {
  const m = new Map();
  function walk(node) {
    m.set(node.id, node);
    for (const c of node.children ?? []) walk(c);
  }
  for (const r of tree) walk(r);
  return m;
}

// Détail transmis au panneau de drill-down (clic cellule du tableau ou ligne de la vue mois).
export function buildCellDetail(node, col) {
  return {
    from:        col.period_start,
    to:          col.period_end,
    categoryIds: collectDescendantIds(node),
    mode:        'both',
    title:       node.name,
    subtitle:    formatPeriodRange(col.period_start, col.period_end),
    flowType:    node.flow_type ?? 'expense',
  };
}

export function buildUnclassifiedDetail(col) {
  return {
    from:         col.period_start,
    to:           col.period_end,
    unclassified: true,
    mode:         'actuals',
    title:        'Transactions à catégoriser',
    subtitle:     formatPeriodRange(col.period_start, col.period_end),
  };
}

// Séries TVA (une valeur par colonne). Nouveau contrat : 4 arrays Réel/Prévi ;
// repli sur l'ancien contrat (collected_per_period) pendant la fenêtre de déploiement.
export function extractVatSeries(vs) {
  if (!vs) return null;
  const hasNewContract =
    Array.isArray(vs.collected_actual_per_period) ||
    Array.isArray(vs.collected_forecast_per_period);
  const nonZero = (arr) => arr.some((v) => v !== 0 && v != null);
  const collectedActual    = vs.collected_actual_per_period    ?? (hasNewContract ? [] : vs.collected_per_period   ?? []);
  const collectedForecast  = vs.collected_forecast_per_period  ?? [];
  const deductibleActual   = vs.deductible_actual_per_period   ?? (hasNewContract ? [] : vs.deductible_per_period  ?? []);
  const deductibleForecast = vs.deductible_forecast_per_period ?? [];
  return {
    collectedActual,
    collectedForecast,
    deductibleActual,
    deductibleForecast,
    hasCollected:  nonZero(collectedActual) || nonZero(collectedForecast),
    hasDeductible: nonZero(deductibleActual) || nonZero(deductibleForecast),
  };
}
